import React from 'react';
import { useIntl } from 'react-intl';

import { CountryCode } from 'aws-sdk/clients/route53domains';

import { AppDataStoreData, GlobalMessages, StoreSwitcherMessages } from '../../types/Messages';
import { ModalTypes } from '../../types/Modals';
import { IpLocation } from '../../types/common';
import { languageUtils } from '../../utils/languageUtils/languageUtils';
import { messageHooks } from '../messageHooks/messageHooks';
import { modalHooks } from '../modalHooks/modalHooks';

export type UseGetStoreSwitcherButtonLabelParams = {
  isNonBlockPart?: boolean;
  isOnlyLanguage?: boolean;
};

export const storeSwitcherHooks = {
  useGetInitialCountryCode(isDefaultCountry = false) {
    const storeData = messageHooks.useSubjectState(AppDataStoreData.StoreData);
    const countriesCombinations = storeData?.countriesCombinations || null;
    const ipLocation = messageHooks.useSubjectState(GlobalMessages.IpLocation);

    return React.useMemo(() => {
      const country = countriesCombinations?.find(
        (countryElement) => countryElement?.country === ipLocation?.country_code,
      )?.country;
      if (isDefaultCountry) return CONFIG.cadeaubonGraphql.country;
      if (country) return country;
      return null;
    }, [countriesCombinations, ipLocation?.country_code, isDefaultCountry]);
  },
  useSetStates() {
    const setIpLocation = messageHooks.useNext(GlobalMessages.IpLocation);
    const setSelectedCountry = messageHooks.useNext(StoreSwitcherMessages.SelectedCountry);

    return React.useCallback((value: IpLocation | null) => {
      setIpLocation(value);
      setSelectedCountry(value?.country_code ?? null);
    }, [setIpLocation, setSelectedCountry]);
  },
  useIpLocation() {
    const ipLocation = messageHooks.useSubjectState(GlobalMessages.IpLocation);
    const fetchIpLocation = storeSwitcherHooks.useFetchIpLocation();
    const setStates = storeSwitcherHooks.useSetStates();

    React.useEffect(() => {
      if (!ipLocation) {
        fetchIpLocation().then(setStates);
      }
    }, [fetchIpLocation, ipLocation, setStates]);

    return {
      countryCode: ipLocation?.country_code ?? null,
    };
  },
  useFetchIpLocation() {
    return React.useCallback(async () => {
      const response = await fetch('/ip');
      return await response.json();
    }, []);
  },
  useFormatDisplayLabelCallback() {
    const intl = useIntl();
    return React.useCallback((value: CountryCode | string, isLanguage?: boolean) => {
      const language = intl.formatDisplayName(value, { type: 'language' });
      const region = intl.formatDisplayName(value, { type: 'region' });
      if (isLanguage) {
        return value.length ? language : '';
      }
      return value.length ? region : '';
    }, [intl]);
  },
  useGetStoreSwitcherButtonLabel({
    isNonBlockPart = false,
    isOnlyLanguage,
  }: UseGetStoreSwitcherButtonLabelParams) {
    const initialCountryCode = storeSwitcherHooks.useGetInitialCountryCode(isNonBlockPart);
    const lang = messageHooks.useSubjectState(GlobalMessages.Language);
    const language = !lang
      ? languageUtils.getDefaultLanguage().toUpperCase()
      : lang.toUpperCase();
    const displayFormatLabel = storeSwitcherHooks.useFormatDisplayLabelCallback();
    const intl = useIntl();

    return React.useMemo(() => {
      if (isOnlyLanguage) {
        return language;
      }
      const fullCountryName = displayFormatLabel(initialCountryCode || '');
      if (initialCountryCode && fullCountryName) {
        const list = [isNonBlockPart ? language : initialCountryCode, fullCountryName];
        return intl.formatList(list, {
          style: 'short',
          type: 'unit',
        });
      }
      return '';
    }, [displayFormatLabel, initialCountryCode, intl, isNonBlockPart, isOnlyLanguage, language]);
  },
  useStoreRedirect() {
    const initialCountryCode = storeSwitcherHooks.useGetInitialCountryCode();
    const storeData = messageHooks.useSubjectState(AppDataStoreData.StoreData);

    return React.useCallback(() => {
      if (initialCountryCode) {
        const countriesCombinations = storeData?.countriesCombinations || null;
        const countryCombination = countriesCombinations?.find(
          (countryCombination) => countryCombination.country === initialCountryCode,
        );
        if (countryCombination) {
          window.location.href = countryCombination.url;
        }
      }
    }, [initialCountryCode, storeData?.countriesCombinations]);
  },
  useGetStatesForOpenStoreSwitcherModal() {
    const setIsNonBlockPart = messageHooks.useNext(StoreSwitcherMessages.isNonBlockPart);
    return {
      setIsNonBlockPart,
    };
  },
  useOpenStoreSwitcherModal(
    isNonBlockPart?: boolean,
  ) {
    const { setIsNonBlockPart } = storeSwitcherHooks. useGetStatesForOpenStoreSwitcherModal();
    const { onModalOpen } = modalHooks.useModal(ModalTypes.StoreSwitcherModal);
    return React.useCallback(() => {
      isNonBlockPart && setIsNonBlockPart(true);
      onModalOpen();
    }, [isNonBlockPart, onModalOpen, setIsNonBlockPart]);
  },
};
